import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Badge } from "./ui/badge";
import { Upload, FileText, Trash2, CheckCircle2, Clock, XCircle, ShieldCheck, Eye, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const DOC_KIND_LABELS = {
  national_id: "National ID",
  kra_pin: "KRA PIN Certificate",
  good_conduct: "Certificate of Good Conduct",
  cv_resume: "CV / Résumé",
  certificate: "Training Certificate",
  reference_letter: "Reference Letter",
  other: "Other",
};
const REQUIRED_KINDS = ["national_id", "good_conduct", "cv_resume"];

const kindColor = (status) => ({
  approved: "bg-[#1E3A8A] text-white hover:bg-[#1E3A8A]",
  rejected: "bg-[#B91C1C] text-white hover:bg-[#B91C1C]",
  pending: "bg-amber-100 text-amber-900 hover:bg-amber-100",
}[status] || "bg-gray-200 text-gray-700");

const KindIcon = ({ status }) => {
  if (status === "approved") return <CheckCircle2 className="w-4 h-4 text-[#1E3A8A]" />;
  if (status === "rejected") return <XCircle className="w-4 h-4 text-[#B91C1C]" />;
  return <Clock className="w-4 h-4 text-amber-600" />;
};

export default function StaffDocuments() {
  const [docs, setDocs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [kind, setKind] = useState("national_id");
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/documents/mine").then((r) => setDocs(r.data));
    api.get("/documents/requests/mine").then((r) => setRequests(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const uploadedKinds = new Set(docs.map((d) => d.kind));
  const missingRequired = REQUIRED_KINDS.filter((k) => !uploadedKinds.has(k));

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a file first");
    if (f.size > 10 * 1024 * 1024) return toast.error("File too large (max 10 MB)");
    const fd = new FormData();
    fd.append("file", f);
    fd.append("kind", kind);
    fd.append("title", title || f.name);
    setUploading(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — awaiting KSB verification");
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const viewUrl = (d) => {
    const token = localStorage.getItem("ksb_token") || localStorage.getItem("esb_token");
    return `${api.defaults.baseURL}/documents/${d.id}/download?auth=${token}`;
  };

  return (
    <div>
      {requests.filter((r) => r.status === "pending").length > 0 && (
        <div className="mb-6 bg-[#7C3AED]/10 border border-[#7C3AED]/30 rounded-md p-4">
          <div className="font-display font-semibold text-[#7C3AED] flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> KSB has requested documents from you
          </div>
          <div className="mt-3 space-y-3">
            {requests.filter((r) => r.status === "pending").map((r) => (
              <div key={r.id} className="bg-white border border-[#E4E7F5] rounded-md p-3">
                <div className="text-xs text-[#5B6478]">Requested by {r.created_by_name} · {new Date(r.created_at).toLocaleDateString()}</div>
                <div className="text-sm mt-1"><strong>Please upload:</strong> {r.requested_kinds.map((k) => DOC_KIND_LABELS[k] || k).join(" · ")}</div>
                {r.note && <div className="text-sm mt-1 text-[#5B6478]">"{r.note}"</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {missingRequired.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div>
            <div className="font-display font-semibold text-amber-900">Missing verification documents</div>
            <p className="text-sm text-amber-800 mt-1">
              To unlock the Verified badge, please upload:{" "}
              <span className="font-semibold">{missingRequired.map((k) => DOC_KIND_LABELS[k]).join(" · ")}</span>
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E4E7F5] rounded-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-[#1E3A8A]" />
          <h3 className="font-display text-lg font-bold">Upload verification document</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label className="label-eyebrow">Type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="doc-kind" className="mt-2 border-[#E4E7F5]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(DOC_KIND_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Title (optional)</Label>
            <Input data-testid="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2 border-[#E4E7F5]" placeholder="e.g. Front side ID" />
          </div>
          <div>
            <Label className="label-eyebrow">File (PDF, JPG, PNG · max 10 MB)</Label>
            <Input data-testid="doc-file" ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="mt-2 border-[#E4E7F5]" />
          </div>
        </div>
        <Button
          data-testid="doc-upload-btn"
          onClick={upload}
          disabled={uploading}
          className="mt-5 bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]"
        >
          <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading..." : "Upload document"}
        </Button>
      </div>

      <div className="mt-8">
        <h3 className="font-display text-lg font-bold mb-4">My documents ({docs.length})</h3>
        {docs.length === 0 && (
          <div className="text-center py-12 border border-dashed border-[#E4E7F5] rounded-md text-[#5B6478]">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
            No documents uploaded yet.
          </div>
        )}
        <div className="space-y-3">
          {docs.map((d) => (
            <div key={d.id} className="bg-white border border-[#E4E7F5] rounded-md p-4 flex items-start justify-between gap-3" data-testid={`doc-row-${d.id}`}>
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <FileText className="w-8 h-8 text-[#1E3A8A] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{d.title || d.original_filename}</div>
                  <div className="text-xs text-[#5B6478] mt-1">
                    {DOC_KIND_LABELS[d.kind]} · {Math.round((d.size || 0) / 1024)} KB · uploaded {new Date(d.created_at).toLocaleDateString()}
                  </div>
                  {d.admin_notes && (
                    <div className="text-xs text-[#5B6478] mt-2 bg-[#EEF0FA] rounded p-2">
                      <span className="label-eyebrow">Admin note:</span> {d.admin_notes}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className={`capitalize ${kindColor(d.status)}`}>
                  <KindIcon status={d.status} />
                  <span className="ml-1">{d.status}</span>
                </Badge>
                <a href={viewUrl(d)} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm" data-testid={`doc-view-${d.id}`} className="border-[#E4E7F5]">
                    <Eye className="w-3 h-3" />
                  </Button>
                </a>
                {d.status !== "approved" && (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`doc-delete-${d.id}`}
                    className="border-[#B91C1C] text-[#B91C1C]"
                    onClick={() => remove(d.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
